import { useEffect, useRef, useState } from 'react';

import { useAfterMount } from '@yandex-360/orb-components/dist/hooks/useAfterMount';
import { AudioHandler, getDiagnosticAudioLevelAnalyzer, States } from '@yandex-360/telemost-sdk/audio-diagnostics';
import { LoggableError } from '@yandex-int/messenger.errors';
import { useSelector } from 'react-redux';

import { getAudioContext } from '../../../helpers/audio-context';
import { browser } from '../../../helpers/browser-capabilities';
import { createLogger } from '../../../helpers/loggerFacade';
import { useActions } from '../../../hooks';
import {
    getAudioInputDeviceSelector,
    getAudioOutputDeviceSelector,
    setIsAudioInputDiagnosticRunAction,
} from '../../settings';

const audioContext = getAudioContext();
const diagnosticAnalyzer = getDiagnosticAudioLevelAnalyzer(audioContext);
const INPUT_TIMEOUT = 5000;
const logger = createLogger('use-input-audio-handler');
export const useInputAudioHandler = () => {
    const { setIsAudioInputDiagnosticRun } = useActions({
        setIsAudioInputDiagnosticRun: setIsAudioInputDiagnosticRunAction,
    });
    const currentOutputDeviceId = useSelector(getAudioOutputDeviceSelector)?.deviceId;
    const currentInputDeviceId = useSelector(getAudioInputDeviceSelector)?.deviceId;

    const audioHandlerRef = useRef<AudioHandler | null>(null);
    const stopRecordTimeout = useRef<NodeJS.Timeout | null>(null);
    const [audioHandlerState, setAudioHandlerState] = useState<States | null>(null);

    useEffect(() => {
        if (!audioContext || !diagnosticAnalyzer) {
            return;
        }

        if (!audioHandlerRef.current) {
            audioHandlerRef.current = new AudioHandler(audioContext, diagnosticAnalyzer);
        }

        const stopRecord = () => {
            stopRecordTimeout.current = setTimeout(() => audioHandlerRef.current?.stopRecording(), INPUT_TIMEOUT);
        };

        const startPlaying = () => {
            if (stopRecordTimeout.current) {
                clearTimeout(stopRecordTimeout.current);
            }
            audioHandlerRef.current?.playRecorded(currentOutputDeviceId);
        };

        const onPlayStop = () => {
            setIsAudioInputDiagnosticRun(false);
        };

        audioHandlerRef.current.onStateChanged.addListener(setAudioHandlerState);
        audioHandlerRef.current.onRecordStart.addListener(stopRecord);
        audioHandlerRef.current.onRecordStop.addListener(startPlaying);
        audioHandlerRef.current.onPlayStop.addListener(onPlayStop);

        setAudioHandlerState(audioHandlerRef.current.state);
        return () => {
            audioHandlerRef.current?.onStateChanged.removeListener(setAudioHandlerState);
            audioHandlerRef.current?.onRecordStart.removeListener(stopRecord);
            audioHandlerRef.current?.onRecordStop.removeListener(startPlaying);
            audioHandlerRef.current?.onPlayStop.removeListener(onPlayStop);
        };
    }, [currentOutputDeviceId, currentInputDeviceId, setIsAudioInputDiagnosticRun]);
    useEffect(() => {
        return () => {
            // unmount component
            if (stopRecordTimeout.current) {
                clearTimeout(stopRecordTimeout.current);
            }
            setIsAudioInputDiagnosticRun(false);
            audioHandlerRef.current?.destroy();
        };
    }, [setIsAudioInputDiagnosticRun]);
    useAfterMount(() => {
        if (
            browser()?.isSafariBased() &&
            (!audioHandlerRef.current || audioHandlerRef.current.state === States.PLAYING)
        ) {
            setIsAudioInputDiagnosticRun(false);
            audioHandlerRef.current?.stopPlayback();
            return;
        }

        audioHandlerRef.current?.changeOutputDevice(currentOutputDeviceId ?? '').catch((error) => {
            logger.error(error, {
                method: 'changeOutputDevice',
            });
            if (!audioHandlerRef.current) {
                logger.warn(new Error('AudioHandler is not defined') as LoggableError);
                return;
            }
            setIsAudioInputDiagnosticRun(false);
            audioHandlerRef.current.emergencyStop();
        });
    }, [currentOutputDeviceId]);

    return {
        audioHandlerState,
        audioHandlerRef,
    };
};
